import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getSchoolUrl } from "@/lib/slug";
import { GraduationCap } from "lucide-react";

/**
 * Jenjang tab picker rendered on the school detail page (per brief §Phase 4).
 *
 * Behaviour:
 *   • Fetches sibling units under the SAME campus_id from `/api/schools/{id}/siblings`.
 *   • Renders one tab per available jenjang (TK · SD · SMP · SMA).
 *   • The tab for the currently-viewed school is highlighted.
 *   • Clicking a different-jenjang tab navigates to that sibling's slug URL.
 *   • If the school has no sibling units, the component renders NOTHING (no
 *     visually-lonely single tab).
 */
const JENJANG_ORDER = ["TK", "SD", "SMP", "SMA"];

export default function JenjangTabs({ school }) {
  const nav = useNavigate();
  const [siblings, setSiblings] = useState([]);

  useEffect(() => {
    if (!school?.school_id) return;
    api.get(`/schools/${school.school_id}/siblings`)
      .then((r) => setSiblings(r.data?.siblings || []))
      .catch(() => setSiblings([]));
  }, [school?.school_id]);

  // Group siblings by their primary jenjang so we can build one tab per level.
  const buckets = React.useMemo(() => {
    const map = {};
    for (const s of siblings) {
      const lvl = (Array.isArray(s.education_levels) && s.education_levels[0])
        || (typeof s.level === "string" ? s.level.toUpperCase() : null);
      if (!lvl) continue;
      const norm = String(lvl).toUpperCase();
      if (!JENJANG_ORDER.includes(norm)) continue;
      // First-wins: if the campus has two TK units, keep the first alphabetically.
      if (!map[norm]) map[norm] = s;
    }
    return map;
  }, [siblings]);

  const availableLevels = JENJANG_ORDER.filter((lvl) => buckets[lvl]);
  if (availableLevels.length < 2) return null; // hide when there's nothing to flip to

  return (
    <div className="mb-4" data-testid="jenjang-tabs">
      <div className="inline-flex items-center rounded-full bg-white border border-[#EAE7E0] p-1 shadow-[0_1px_4px_-2px_rgba(31,41,55,0.08)]">
        <span className="pl-3 pr-2 text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold inline-flex items-center gap-1">
          <GraduationCap size={11} /> Jenjang
        </span>
        {availableLevels.map((lvl) => {
          const sib = buckets[lvl];
          const isCurrent = sib.school_id === school.school_id;
          return (
            <button
              type="button"
              key={lvl}
              aria-pressed={isCurrent}
              data-testid={`jenjang-tab-${lvl.toLowerCase()}`}
              data-active={isCurrent}
              onClick={() => { if (!isCurrent) nav(getSchoolUrl(sib)); }}
              className={
                "px-4 py-1.5 rounded-full text-sm transition-colors " +
                (isCurrent
                  ? "bg-[#1E3A8A] text-white font-medium pointer-events-none"
                  : "text-[#4B5563] hover:text-[#1F2937] hover:bg-[#F3F1ED]")
              }
              title={isCurrent ? `Anda sedang melihat unit ${lvl}` : `Buka unit ${lvl}: ${sib.name}`}
            >
              {lvl}
            </button>
          );
        })}
      </div>
    </div>
  );
}
